import { createPortal } from 'preact/compat';
import { useEffect, useRef, useState } from 'preact/hooks';
import { Input } from './input';
import { ScrollArea } from './scroll-area';
import { IconChevronDown, IconSearch } from './icons';
import { cn } from '../../lib/utils';
import { getCanvasScale, onCanvasScaleChange } from '../../utils/uiScale';
import { t } from '../../localization/t';
import {
  GOLD, ACCENT_GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED,
  DIALOG_BG, DIALOG_BORDER, BEVELED_BTN, INPUT_STYLE,
} from '../../lib/medievalTheme';

const MENU_VIEWPORT_PADDING = 16;
const MENU_VERTICAL_OFFSET = 8;

// Medieval-themed surface tokens for the Select component. Inline
// styles are used here (instead of Tailwind utility classes) so the
// component renders identically regardless of which CSS layer is
// loaded. And so the rest of the codebase's gold/parchment palette
// stays the single source of truth.
const SELECT_TRIGGER_STYLE = {
  ...BEVELED_BTN,
  borderRadius: 'var(--radius-md)',
  color: TEXT_PRIMARY,
  height: '2.5cqw',
  // Solid base layer underneath the textures so nothing behind the
  // trigger can bleed through (the rest of the medieval UI assumes
  // dialogs sit on opaque surfaces).
  backgroundColor: '#0e0a06',
};

// Menu padding (the `p-1` on the surface) and the radius of everything
// sitting one padding-step inside it (search field, option rows). The menu's
// own radius is concentric with those: outer = inner + pad. Numbers match
// the sidebar context menu (`ui/context-menu.jsx`) so both surfaces read as
// the same component family.
const MENU_PAD = '0.25cqw';
const INNER_RADIUS = 'var(--radius-lg)';
const MENU_RADIUS = `calc(${INNER_RADIUS} + ${MENU_PAD})`;

const SELECT_MENU_STYLE = {
  background: DIALOG_BG,
  backgroundColor: '#0e0a06',
  border: `1px solid ${DIALOG_BORDER}`,
  borderRadius: MENU_RADIUS,
  boxShadow: '0 12px 32px rgba(0,0,0,0.6), 0 0 24px rgba(180,140,60,0.08)',
  color: TEXT_BODY,
  maxHeight: 'min(28cqw, calc(100cqh - 40px))',
  display: 'flex',
  flexDirection: 'column',
};

const SELECT_SEARCH_STYLE = {
  ...INPUT_STYLE,
  backgroundColor: '#0e0a06',
  borderRadius: INNER_RADIUS,
  color: TEXT_PRIMARY,
  height: '2cqw',
};

const OPTION_BASE_STYLE = {
  borderRadius: INNER_RADIUS,
  color: TEXT_BODY,
  textShadow: '0 1px 2px rgba(0,0,0,0.5)',
  transition: 'background-color 120ms ease, color 120ms ease, border-color 120ms ease',
  // Transparent border at rest keeps the row geometry identical to the
  // active state (which adds a 1px gold border), so toggling between
  // states doesn't nudge content sideways by a pixel.
  border: '1px solid transparent',
};

const OPTION_ACTIVE_STYLE = {
  ...OPTION_BASE_STYLE,
  background: `${GOLD} 0.18)`,
  color: TEXT_PRIMARY,
  border: `1px solid ${GOLD} 0.4)`,
};

const OPTION_HIGHLIGHTED_STYLE = {
  ...OPTION_BASE_STYLE,
  background: `${GOLD} 0.1)`,
  color: TEXT_PRIMARY,
};

function matchesOptionSearch(option, query) {
  const normalizedQuery = String(query || '').trim().toLowerCase();

  if (!normalizedQuery) {
    return true;
  }

  return [option?.label, option?.description, option?.value]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(normalizedQuery));
}

function Select({
  ariaLabel,
  className,
  disabled = false,
  menuAlign = 'start',
  menuClassName,
  menuPreferredWidth = null,
  menuSearchAriaLabel = '',
  menuSearchPlaceholder = t('Search options'),
  noOptionsMessage = t('No options'),
  onValueChange = () => {},
  options = [],
  placeholder = t('Select an option'),
  portalMenu = false,
  renderOptionAccessory,
  searchable = false,
  triggerClassName,
  // Inline overrides for the trigger box (height, radius, colors). Needed
  // because SELECT_TRIGGER_STYLE is inline and always beats a class.
  triggerStyle,
  triggerLabelClassName = 'truncate',
  triggerVariant = 'outline',
  value = '',
}) {
  const [open, setOpen] = useState(false);
  const [menuQuery, setMenuQuery] = useState('');
  const [menuPosition, setMenuPosition] = useState(null);
  // Index of the keyboard-highlighted option in filteredOptions.
  //   searchable=true  → defaults to 0 so pressing Enter in the search
  //                      box picks the top match (autocomplete affordance).
  //   searchable=false → defaults to -1 so a freshly-opened plain
  //                      dropdown shows no highlight until the user
  //                      hovers or arrow-keys into it. Auto-highlighting
  //                      the first row before any interaction looks
  //                      like a misclick.
  const [highlightedIndex, setHighlightedIndex] = useState(searchable ? 0 : -1);
  // menuPreferredWidth is design-pixels; scale with the canvas.
  const [canvasScale, setCanvasScale] = useState(getCanvasScale());
  useEffect(() => onCanvasScaleChange(setCanvasScale), []);
  const scaledPreferredWidth = menuPreferredWidth != null ? menuPreferredWidth * canvasScale : null;
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const optionRefs = useRef([]);
  const selectedOption = options.find((option) => option.value === value) || null;
  const filteredOptions = searchable ? options.filter((option) => matchesOptionSearch(option, menuQuery)) : options;

  function updateMenuPosition() {
    if (!portalMenu || typeof window === 'undefined') {
      return;
    }

    const trigger = rootRef.current;
    const menu = menuRef.current;

    if (!trigger || !menu) {
      return;
    }

    const triggerRect = trigger.getBoundingClientRect();
    const menuRect = menu.getBoundingClientRect();
    const availableWidth = Math.max(0, window.innerWidth - MENU_VIEWPORT_PADDING * 2);
    const desiredWidth = Math.max(
      triggerRect.width,
      scaledPreferredWidth ?? menuRect.width ?? triggerRect.width
    );
    const width = Math.min(desiredWidth, availableWidth);
    const preferredLeft = menuAlign === 'end' ? triggerRect.right - width : triggerRect.left;
    const left = Math.max(
      MENU_VIEWPORT_PADDING,
      Math.min(preferredLeft, window.innerWidth - MENU_VIEWPORT_PADDING - width)
    );
    const measuredHeight = menuRect.height || menu.offsetHeight || 0;
    const spaceBelow = Math.max(
      0,
      window.innerHeight - triggerRect.bottom - MENU_VIEWPORT_PADDING - MENU_VERTICAL_OFFSET
    );
    const spaceAbove = Math.max(0, triggerRect.top - MENU_VIEWPORT_PADDING - MENU_VERTICAL_OFFSET);
    const shouldOpenUpward = measuredHeight > 0 && spaceBelow < measuredHeight && spaceAbove > spaceBelow;
    const preferredTop = shouldOpenUpward
      ? triggerRect.top - MENU_VERTICAL_OFFSET - measuredHeight
      : triggerRect.bottom + MENU_VERTICAL_OFFSET;
    const top = measuredHeight > 0
      ? Math.max(
          MENU_VIEWPORT_PADDING,
          Math.min(preferredTop, window.innerHeight - MENU_VIEWPORT_PADDING - measuredHeight)
        )
      : Math.max(
          MENU_VIEWPORT_PADDING,
          Math.min(preferredTop, window.innerHeight - MENU_VIEWPORT_PADDING)
        );

    setMenuPosition({
      left,
      top,
      width,
    });
  }

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    function handlePointerDown(event) {
      if (rootRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) {
        return;
      }

      setOpen(false);
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    }

    function handleViewportChange() {
      updateMenuPosition();
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    if (portalMenu) {
      window.addEventListener('resize', handleViewportChange);
      window.addEventListener('scroll', handleViewportChange, true);
    }

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      if (portalMenu) {
        window.removeEventListener('resize', handleViewportChange);
        window.removeEventListener('scroll', handleViewportChange, true);
      }
    };
  }, [open, portalMenu]);

  useEffect(() => {
    if (!open) {
      setMenuQuery('');
      setMenuPosition(null);
      setHighlightedIndex(0);
    }
  }, [open]);

  // Reset the highlight to the first match whenever the filter changes
  // so a single Enter always picks the top result. Also clamp it within
  // the new filteredOptions length so an out-of-range index doesn't
  // linger after a narrowing filter.
  useEffect(() => {
    setHighlightedIndex((idx) =>
      filteredOptions.length === 0 ? 0 : Math.min(idx, filteredOptions.length - 1)
    );
  }, [filteredOptions.length, menuQuery]);

  // Scroll the highlighted option into view as the user navigates with
  // the arrow keys. block: 'nearest' avoids fighting with the browser's
  // default scroll behavior on the first reveal.
  useEffect(() => {
    if (!open) return;
    const el = optionRefs.current[highlightedIndex];
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex, open]);

  // Reset the highlight every time the menu re-opens so a stale index
  // from the previous session can't pre-light a row on the new open.
  useEffect(() => {
    if (open) setHighlightedIndex(searchable ? 0 : -1);
  }, [open, searchable]);

  useEffect(() => {
    if (!open || !portalMenu) {
      return undefined;
    }

    updateMenuPosition();
    return undefined;
  }, [filteredOptions.length, menuAlign, menuPreferredWidth, open, portalMenu, searchable]);

  const menuContent = open ? (
    <div
      ref={menuRef}
      role="menu"
      aria-label={ariaLabel}
      className={cn(
        'vh-menu-in',
        portalMenu
          ? 'fixed z-[140] p-1'
          : 'absolute left-0 top-full z-30 mt-2 w-full p-1',
        menuClassName
      )}
      style={
        portalMenu
          ? {
              ...SELECT_MENU_STYLE,
              left: `${menuPosition?.left ?? MENU_VIEWPORT_PADDING}px`,
              top: `${menuPosition?.top ?? MENU_VIEWPORT_PADDING}px`,
              minWidth: `${menuPosition?.width ?? scaledPreferredWidth ?? 0}px`,
              visibility: menuPosition ? 'visible' : 'hidden',
            }
          : SELECT_MENU_STYLE
      }
    >
      {searchable ? (
        <div className="mb-1">
          <label className="relative block">
            <IconSearch
              className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2"
              style={{ color: TEXT_MUTED }}
            />
            <Input
              autoFocus
              type="search"
              role="searchbox"
              aria-label={menuSearchAriaLabel || t('Search {label}', { label: ariaLabel })}
              value={menuQuery}
              onInput={(event) => setMenuQuery(event.currentTarget.value)}
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown') {
                  event.preventDefault();
                  if (filteredOptions.length === 0) return;
                  setHighlightedIndex((idx) => (idx + 1) % filteredOptions.length);
                } else if (event.key === 'ArrowUp') {
                  event.preventDefault();
                  if (filteredOptions.length === 0) return;
                  setHighlightedIndex((idx) => (idx - 1 + filteredOptions.length) % filteredOptions.length);
                } else if (event.key === 'Enter') {
                  if (filteredOptions.length === 0) return;
                  event.preventDefault();
                  const safeIdx = Math.min(highlightedIndex, filteredOptions.length - 1);
                  const choice = filteredOptions[safeIdx];
                  if (choice) {
                    onValueChange(choice.value);
                    setOpen(false);
                  }
                }
              }}
              placeholder={menuSearchPlaceholder}
              className="pl-9 shadow-none border-0"
              style={SELECT_SEARCH_STYLE}
            />
          </label>
        </div>
      ) : null}

      {/*
        Overlay scrollbar: -mr-1 pulls the scroll area into the menu's
        right padding so the thumb lives in that gutter; pr-1 on the
        viewport keeps option insets symmetric whether or not it scrolls.
      */}
      <ScrollArea className="flex-1 -mr-1" viewportClassName="pr-1">
      {filteredOptions.length === 0 ? (
        <div className="px-1.5 py-1.5 text-xs" style={{ color: TEXT_MUTED }}>{noOptionsMessage}</div>
      ) : null}

      {filteredOptions.map((option, optionIdx) => {
        const isActive = option.value === value;
        const isHighlighted = highlightedIndex >= 0
          && optionIdx === Math.min(highlightedIndex, filteredOptions.length - 1);
        const accessory = renderOptionAccessory?.({
          option,
          isActive,
          closeMenu: () => setOpen(false),
        });
        const baseOptionStyle = isActive
          ? OPTION_ACTIVE_STYLE
          : isHighlighted
            ? OPTION_HIGHLIGHTED_STYLE
            : OPTION_BASE_STYLE;
        // First option sits flush against the menu's top padding (or
        // the search header); every subsequent option gets a small
        // top margin so the rounded backgrounds don't fuse into a
        // single block on hover/active.
        const optionStyle = optionIdx > 0
          ? { ...baseOptionStyle, marginTop: '0.25cqw' }
          : baseOptionStyle;
        const optionButton = (
          <button
            key={option.value}
            ref={(el) => { optionRefs.current[optionIdx] = el; }}
            type="button"
            role="menuitemradio"
            aria-checked={isActive}
            data-highlighted={isHighlighted ? 'true' : undefined}
            className="flex w-full min-w-0 items-center gap-2 px-1.5 py-1.5 text-left text-xs font-medium cursor-pointer"
            style={optionStyle}
            onMouseEnter={() => setHighlightedIndex(optionIdx)}
            onClick={() => {
              onValueChange(option.value);
              setOpen(false);
            }}
          >
            <span
              className="flex size-4 shrink-0 items-center justify-center"
              style={{ color: isActive ? ACCENT_GOLD : 'transparent' }}
            >
              <svg viewBox="0 0 14 14" fill="none" style={{ width: '0.875cqw', height: '0.875cqw' }}><path d="M3 7l3 3 5-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </span>
            <span className="min-w-0 flex-1">
              <span className="block whitespace-nowrap">{option.label}</span>
              {option.description ? (
                <span className="mt-0.5 block whitespace-nowrap text-[0.625cqw]" style={{ color: TEXT_MUTED }}>{option.description}</span>
              ) : null}
            </span>
          </button>
        );

        if (!accessory) {
          return optionButton;
        }

        return (
          <div key={option.value} className="flex items-center gap-2">
            {optionButton}
            {accessory}
          </div>
        );
      })}
      </ScrollArea>
    </div>
  ) : null;

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <button
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        className={cn(
          'flex h-10 w-full items-center justify-between px-1.5 text-sm font-normal cursor-pointer transition-all',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          triggerClassName
        )}
        style={{ ...SELECT_TRIGGER_STYLE, ...triggerStyle }}
        onClick={() => setOpen((current) => !current)}
      >
        <span
          className={cn('text-left', triggerLabelClassName)}
          style={{ color: selectedOption ? TEXT_PRIMARY : TEXT_MUTED }}
        >
          {selectedOption?.triggerLabel || selectedOption?.label || placeholder}
        </span>
        <IconChevronDown
          className={cn('size-4 transition-transform', open && 'rotate-180')}
          style={{ color: ACCENT_GOLD }}
        />
      </button>

      {portalMenu && menuContent && typeof document !== 'undefined' ? createPortal(menuContent, document.body) : menuContent}
    </div>
  );
}

export { Select };
